import React, { FC, useState } from 'react';
import styles from './organisationEditNode.module.scss';
import { Col, Input, Row, Switch, Tooltip, Typography } from 'antd';
import { ReactComponent as AddIcon } from 'assets/icons/Add Organisation.svg';
import clsx from 'clsx';
import { OrganisationNodeProps } from '../OrganisationViewNode';
import { SwitchChangeEventHandler } from 'antd/lib/switch';
import { allowOnlyPercentages } from 'shared/utils/convertFormData';
import { Field, FieldProps, useField, useFormikContext } from 'formik';
import SelectOrganisationModal from '../../EditOrganisationMap/SelectNewOrganisationModal';
import { NewNodeType } from 'enums/organisationMapEnums';
import ValidationModal from '../../EditOrganisationMap/OrganizationDisableValidationModal';
import {
  handleParentChildHeirarchy,
  organisationSorter,
} from '../../EditOrganisationMap/editOrganisationMap.util';
import { OrganisationMapPayload } from 'models/CompanySetup/organisationPayload.model';
import { Organization } from 'models/CompanySetup/organization.model';
import OptionsDropdown from 'shared/components/OptionsDropdown';
import Notification from 'shared/components/Notification';
import { NotificationTypes } from 'enums/notificationTypes';

const OrganisationEditNode: FC<OrganisationNodeProps> = ({
  organisation,
  index,
  organisationList,
  organisationListMeta,
  year,
  validateOrganisationStatus,
  setOrganisationMapData,
  canEdit = true,
}) => {
  const shareFieldName = `organizationMap[${index}].organisationYearWise.organizationShare`;
  const { name, organisationYearWise } = organisation;
  const isParent = !organisationYearWise?.parentName;
  const [_, shareFieldMeta] = useField(shareFieldName);
  const [additionType, setAdditionType] = useState<NewNodeType>();
  const [visible, setVisible] = React.useState<boolean>(false);
  const { setFieldValue } = useFormikContext<OrganisationMapPayload>();

  const handleToggleVisible = () => setVisible(prev => !prev);

  const deleteHandler = () => {
    if (!canEdit) {
      Notification({
        message: "You don't have admin access to add location and year",
        description: '',
        type: NotificationTypes.WARNING,
      });
      return;
    }
    if (!setOrganisationMapData) return;
    const modifiedList = handleParentChildHeirarchy(
      organisationList ?? [],
      organisation
    );
    const updatedOrganisationList: Organization[] =
      modifiedList?.filter(item => {
        if (item.name !== organisation.name) return item;
      }) || [];
    setOrganisationMapData(prev => {
      return {
        ...prev,
        organizationMap: organisationSorter(updatedOrganisationList),
      };
    });
    setFieldValue('organizationMap', updatedOrganisationList);
  };

  const handleSwitch: SwitchChangeEventHandler = async (checked, e) => {
    if (!canEdit) {
      Notification({
        message: "You don't have admin access to add location and year",
        description: '',
        type: NotificationTypes.WARNING,
      });
      return;
    }
    if (checked) return;
    if (organisation.id && validateOrganisationStatus) {
      const response = await validateOrganisationStatus(
        { year },
        organisation?.id
      );
      if (!!response) {
        deleteHandler();
      } else handleToggleVisible();
    } else handleToggleVisible();
  };

  const handleAddParent = () => {
    if (!canEdit) {
      Notification({
        message: "You don't have admin access to add location and year",
        description: '',
        type: NotificationTypes.WARNING,
      });
      return;
    }
    setAdditionType(NewNodeType.PARENT);
  };

  const handleAddChild = () => {
    if (!canEdit) {
      Notification({
        message: "You don't have admin access to add location and year",
        description: '',
        type: NotificationTypes.WARNING,
      });
      return;
    }
    setAdditionType(NewNodeType.CHILD);
  };

  const handleCloseSelectModal = () => setAdditionType(undefined);

  const handleSetFieldValue: React.ChangeEventHandler<HTMLInputElement> = e => {
    if (!canEdit) {
      Notification({
        message: "You don't have admin access to add location and year",
        description: '',
        type: NotificationTypes.WARNING,
      });
      return;
    }
    setOrganisationMapData &&
      e.target.value &&
      setOrganisationMapData(prev => {
        return {
          ...prev,
          organizationMap: prev.organizationMap?.map((org, orgIndex) => {
            if (orgIndex == index)
              return {
                ...org,
                organisationYearWise: {
                  ...org.organisationYearWise,
                  organizationShare: Number(e.target.value),
                },
              };
            return org;
          }),
        };
      });
    setFieldValue(shareFieldName, e.target.value);
  };

  return (
    <Field name={shareFieldName}>
      {({ field }: FieldProps) => (
        <>
          <Row
            className={clsx({
              [styles.organisationEditNodeContainer]: true,
              [styles.parentBorder]: isParent,
            })}>
            <Col
              className={styles.addParentContainer}
              onClick={handleAddParent}
              style={{
                opacity: canEdit ? 1 : 0.6,
                cursor: canEdit ? 'pointer' : 'not-allowed',
              }}>
              <AddIcon />
            </Col>
            <Col className={styles.editFieldsWrapper}>
              <Row className={styles.editFields}>
                <Col className={styles.name}>
                  <Tooltip title={name}>
                    <Typography.Paragraph ellipsis={{ rows: 1 }}>
                      {name}
                    </Typography.Paragraph>
                  </Tooltip>
                </Col>
                {isParent ? (
                  ''
                ) : (
                  <>
                    <Col className={styles.switch}>
                      <Switch
                        checked={organisationYearWise?.isActive}
                        onChange={handleSwitch}
                        disabled={!canEdit}
                      />
                    </Col>
                    <Col className={styles.organisationShareWrapper}>
                      <div
                        className={clsx({
                          [styles.organisationShare]: true,
                          [styles.error]:
                            shareFieldMeta.touched && shareFieldMeta.error,
                        })}>
                        <Input
                          {...field}
                          maxLength={5}
                          onKeyDown={allowOnlyPercentages}
                          name={shareFieldName}
                          defaultValue={
                            organisation.organisationYearWise?.organizationShare
                          }
                          onChange={handleSetFieldValue}
                          disabled={!canEdit}
                        />
                        {` %`}
                      </div>
                      {canEdit && (
                        <OptionsDropdown
                          deleteHandler={deleteHandler}
                          className={clsx(styles.menuContainer, {
                            [styles.visible]: !organisation.id,
                          })}
                        />
                      )}
                    </Col>
                  </>
                )}
              </Row>
            </Col>
            <Col
              className={styles.addChildContainer}
              onClick={handleAddChild}
              style={{
                opacity: canEdit ? 1 : 0.6,
                cursor: canEdit ? 'pointer' : 'not-allowed',
              }}>
              <AddIcon />
            </Col>
          </Row>
          {(additionType === NewNodeType.PARENT ||
            additionType === NewNodeType.CHILD) && (
            <SelectOrganisationModal
              isOpen={
                additionType === NewNodeType.PARENT ||
                additionType === NewNodeType.CHILD
              }
              onClose={handleCloseSelectModal}
              currentOrganisation={organisation}
              organisationList={organisationList ?? []}
              organisationListMeta={organisationListMeta ?? []}
              additionType={additionType}
              setOrganisationMapData={setOrganisationMapData}
            />
          )}
          <ValidationModal isOpen={visible} onClose={handleToggleVisible} />
        </>
      )}
    </Field>
  );
};

export default OrganisationEditNode;
